import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import axios from "axios";
import { GlobalState } from "../../../../context/GlobalContext";
import { MenuItem, Select, Switch, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
	divisions,
	paymentModeOptions,
	paymentTermColumns,
	supplierIdParamName,
	supplierTypeOptions,
} from "data/supplierPageData";
import { rootProtectedPath, services } from "data/authData";
import {
	modalCancelBtnSx,
	modalLabelSx,
	modalSaveBtnSx,
	SupplierStatusChip,
	supplierUi,
} from "../../shared/supplierUi";

const parseCsv = (value) =>
	String(value || "")
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);

const normalizeDivision = (value) =>
	String(value || "")
		.toUpperCase()
		.replace(/_/g, " ")
		.replace(/\s+/g, " ")
		.trim();

const isManualSupplierTypeDivision = (productDivision) =>
	parseCsv(productDivision).some((part) => {
		const normalized = normalizeDivision(part);
		return normalized === normalizeDivision(divisions.service) || normalized === normalizeDivision(divisions.capital_goods);
	});

const distinctFactoryTypes = (factories) => {
	const seen = new Set();
	(factories || []).forEach((factory) => {
		parseCsv(factory?.factory_type).forEach((type) => seen.add(type));
	});
	return [...seen];
};

const applySupplierTypeFromFactories = (data) => {
	if (!data || isManualSupplierTypeDivision(data.product_division)) return data;
	const derived = distinctFactoryTypes(data.supplierFactories);
	if (!derived.length) return data;
	return {
		...data,
		supplier_type: derived.join(","),
	};
};

function SupplierInfoModal({
	modalEditable,
	selectedSupplier,
	setSelectedSupplier,
	allSuppliers,
	setAllSuppliers,
	setModalOpen,
	setModalEditable,
}) {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();

	const [companyData, setCompanyData] = useState(selectedSupplier);
	const [loadedSupplier, setLoadedSupplier] = useState(selectedSupplier);
	const navigate = useNavigate();

	const canEditSupplierType = useMemo(
		() => isManualSupplierTypeDivision(companyData?.product_division),
		[companyData?.product_division]
	);

	useEffect(() => {
		setCompanyData(selectedSupplier);
		setLoadedSupplier(selectedSupplier);
		if (!selectedSupplier?.id) return undefined;

		let cancelled = false;
		axios
			.get(`/api/supplier/get_by_id?sup_id=${selectedSupplier.id}`)
			.then((res) => {
				if (cancelled) return;
				const full = applySupplierTypeFromFactories({
					...selectedSupplier,
					...(res.data?.result || {}),
				});
				setLoadedSupplier(full);
				setCompanyData(full);
			})
			.catch(() => {});

		return () => {
			cancelled = true;
		};
	}, [selectedSupplier?.id]);

	const updateSupplier = async (apibody) => {
		await axios
			.put(`/api/supplier/update/${companyData.id}`, apibody)
			.then(() => {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Supplier updated",
					severity: "success",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				const updated = { ...companyData, ...apibody };
				setAllSuppliers([
					updated,
					...allSuppliers.filter((row) => row.id !== selectedSupplier.id),
				]);
				setLoadedSupplier(updated);
				setCompanyData(updated);
				setModalEditable(true);
			})
			.catch(() => {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Unable to update supplier details ",
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			});
	};

	const onFormSubmit = async (e) => {
		e.preventDefault();
		let apibody = {};
		const derivedFactoryTypes = distinctFactoryTypes(companyData?.supplierFactories);
		const supplierTypeValue = canEditSupplierType
			? companyData.supplier_type
			: derivedFactoryTypes.length
			? derivedFactoryTypes.join(",")
			: companyData.supplier_type;

		const missingSupplierType =
			canEditSupplierType && (!supplierTypeValue || supplierTypeValue === "select");
		if (companyData.payment_term === "select" || missingSupplierType) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: missingSupplierType
					? companyData.payment_term === "select"
						? "Select both Type and Payment Term"
						: "Supplier type is mandatory for Service / Capital Good"
					: "Select Payment Term",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		paymentTermColumns &&
			Object.keys(paymentTermColumns).forEach((col) => {
				apibody[col] = companyData[col];
			});
		apibody["supplier_type"] = supplierTypeValue;
		apibody["is_active"] = companyData.is_active;
		if (e.target.id === "approve") apibody["is_approved"] = true;
		await updateSupplier(apibody);
	};

	const fieldSx = {
		width: { xs: "100%", sm: "47%", md: "31%" },
	};

	const handleFullDataClick = () => {
		navigate(
			`/${rootProtectedPath}/${services.supplier_dashboard.path}/supplier_info/company_info?${supplierIdParamName}=${companyData.id}`
		);
	};

	const inputSx = {
		bgcolor: "#fff",
		"& .MuiOutlinedInput-root": { borderRadius: 1.5 },
	};

	return (
		<Box component="form" onSubmit={onFormSubmit} sx={{ width: "100%" }}>
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					gap: 2.5,
					px: { xs: 0, md: 0.5 },
					pb: 1,
				}}>
				<Stack sx={fieldSx} spacing={0.75}>
					<InputLabel sx={modalLabelSx}>
						Supplier type <span style={{ color: "#B42318" }}>*</span>
					</InputLabel>
					<Select
						MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
						value={companyData?.supplier_type ? companyData.supplier_type.split(",") : ["select"]}
						disabled={modalEditable || !canEditSupplierType}
						onChange={(e) =>
							setCompanyData({
								...companyData,
								supplier_type: e.target.value.filter((row) => row !== "select").join(","),
							})
						}
						size="small"
						multiple
						required
						sx={inputSx}>
						<MenuItem value="select">Select</MenuItem>
						{supplierTypeOptions.map((option) => (
							<MenuItem key={option.key} value={option.key}>
								{option.name}
							</MenuItem>
						))}
					</Select>
					{!canEditSupplierType && distinctFactoryTypes(companyData?.supplierFactories).length ? (
						<Typography sx={{ fontSize: 12, color: supplierUi.textSecondary }}>
							Selected from distinct factory types
						</Typography>
					) : null}
				</Stack>

				<Stack sx={fieldSx} spacing={0.75}>
					<InputLabel sx={modalLabelSx}>
						Payment term <span style={{ color: "#B42318" }}>*</span>
					</InputLabel>
					<Select
						MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
						value={companyData?.payment_term || "select"}
						disabled={modalEditable}
						onChange={(e) => setCompanyData({ ...companyData, payment_term: e.target.value })}
						size="small"
						required
						sx={inputSx}>
						<MenuItem value="select">Select</MenuItem>
						<MenuItem value="30%_ADVANCE_70%_ONE_WEEK_FROM_DATE_OF_GRN">30% Advance Payment and 70% One week from Date of GRN</MenuItem>
						<MenuItem value="90_DAYS_OC">90 Days OC</MenuItem>
						<MenuItem value="60_DAYS_OC">60 Days OC</MenuItem>
						<MenuItem value="45_DAYS_OC">45 Days OC</MenuItem>
						<MenuItem value="30_DAYS_OC">30 Days OC</MenuItem>
						<MenuItem value="7_DAYS_OC">7 Days OC</MenuItem>
						<MenuItem value="ADVANCE_PAYMENT">ADVANCE PAYMENT</MenuItem>
					</Select>
				</Stack>

				<Stack sx={fieldSx} spacing={0.75}>
					<InputLabel sx={modalLabelSx}>
						Payment mode <span style={{ color: "#B42318" }}>*</span>
					</InputLabel>
					<Select
						MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
						value={companyData?.[paymentTermColumns.payment_mode.key] || "select"}
						disabled={modalEditable}
						onChange={(e) =>
							setCompanyData({
								...companyData,
								[paymentTermColumns.payment_mode.key]: e.target.value,
							})
						}
						size="small"
						required
						sx={inputSx}>
						<MenuItem value="select">Select</MenuItem>
						{Object.keys(paymentModeOptions || {}).map((row) => {
							const data = paymentModeOptions[row];
							return (
								<MenuItem key={data.key} value={data.key}>
									{data.name}
								</MenuItem>
							);
						})}
					</Select>
				</Stack>

				<Stack sx={{ ...fieldSx, width: { xs: "100%", md: "47%" } }} spacing={0.75}>
					<InputLabel sx={modalLabelSx}>
						THOR merchant email <span style={{ color: "#B42318" }}>*</span>
					</InputLabel>
					<TextField
						size="small"
						type="email"
						value={companyData?.[paymentTermColumns.merchant_email.key] || ""}
						disabled={modalEditable}
						onChange={(e) =>
							setCompanyData({
								...companyData,
								[paymentTermColumns.merchant_email.key]: e.target.value,
							})
						}
						placeholder="example@thehouseofrare.com"
						required
						sx={inputSx}
					/>
				</Stack>

				<Stack
					sx={{
						width: "100%",
						flexDirection: "row",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 2,
						p: 1.5,
						border: `1px solid ${supplierUi.border}`,
						borderRadius: 1.5,
						bgcolor: "#FAFAFA",
					}}>
					<Box>
						<Typography sx={{ fontWeight: 600, fontSize: 14, color: supplierUi.textPrimary }}>
							Supplier status
						</Typography>
						<Typography sx={{ fontSize: 13, color: supplierUi.textSecondary, mt: 0.25 }}>
							Toggle active / inactive for this supplier
						</Typography>
					</Box>
					<Stack direction="row" alignItems="center" spacing={1}>
						<Switch
							disabled={modalEditable}
							checked={!!companyData?.is_active}
							onChange={() => setCompanyData({ ...companyData, is_active: !companyData.is_active })}
							inputProps={{ "aria-label": "supplier status" }}
						/>
						<SupplierStatusChip label={companyData?.is_active ? "Active" : "inactive"} />
					</Stack>
				</Stack>
			</Box>

			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					flexWrap: "wrap",
					gap: 1,
					pt: 2.5,
					mt: 1,
					borderTop: `1px solid ${supplierUi.border}`,
				}}>
				{modalEditable ? (
					<Button variant="outlined" onClick={() => setModalEditable(false)} sx={modalCancelBtnSx}>
						Edit
					</Button>
				) : (
					<Button
						variant="outlined"
						onClick={() => {
							setModalEditable(true);
							setCompanyData(loadedSupplier);
						}}
						sx={modalCancelBtnSx}>
						Cancel edit
					</Button>
				)}
				{!modalEditable ? (
					<Button variant="contained" type="submit" sx={modalSaveBtnSx}>
						Save changes
					</Button>
				) : (
					<Button variant="contained" onClick={handleFullDataClick} sx={modalSaveBtnSx}>
						View full data
					</Button>
				)}
			</Box>
		</Box>
	);
}

export default SupplierInfoModal;
